import React, { useState } from 'react';
import { X, Users, AlertCircle, CheckCircle, Mail, Loader } from 'lucide-react';
import apiService from '../services/api';

interface ImportStripeCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportPreview {
  customers_found: number;
  customers_existing: number;
  customers_to_import: number;
  subscriptions_to_sync: number;
  customers_preview: Array<{
    email: string;
    name: string;
    stripe_customer_id: string;
    trainer_name: string;
    subscriptions: Array<{
      id: string;
      status: string;
      price_id: string;
      product_id: string | null;
    }>;
  }>;
}

interface ImportResult {
  customers_found: number;
  customers_existing: number;
  customers_created: number;
  subscriptions_synced: number;
  emails_sent: number;
  emails_failed: number;
  errors: string[];
}

const ImportStripeCustomersModal: React.FC<ImportStripeCustomersModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<'preview' | 'importing' | 'complete'>('preview');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Call the actual preview endpoint
      const response = await apiService.previewImportStripeCustomers();
      
      if (response.success) {
        setPreview(response.data);
        setStep('preview');
      } else {
        setError('Failed to get preview');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get preview');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    setStep('importing');
    
    try {
      const response = await apiService.importStripeCustomers();
      
      if (response.success) {
        setResult(response.data);
        setStep('complete');
        onSuccess();
      } else {
        setError('Import failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import customers');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('preview');
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Import Existing Stripe Customers
              </h2>
              <p className="text-sm text-gray-600">
                Import customers who are already paying but not in your app
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'preview' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800">
                      What this will do:
                    </h3>
                    <ul className="mt-2 text-sm text-blue-700 space-y-1">
                      <li>• Find all customers in Stripe with active subscriptions</li>
                      <li>• Create user accounts for customers not in your app</li>
                      <li>• Link them to your trainer account</li>
                      <li>• Sync their subscription data</li>
                      <li>• Send password setup emails to new customers</li>
                    </ul>
                  </div>
                </div>
              </div>

              {preview ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Import Preview</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{preview.customers_found}</div>
                      <div className="text-sm text-gray-600">Total customers in Stripe</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{preview.customers_existing}</div>
                      <div className="text-sm text-gray-600">Already in your app</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{preview.customers_to_import}</div>
                      <div className="text-sm text-green-600">Will be imported</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{preview.subscriptions_to_sync}</div>
                      <div className="text-sm text-blue-600">Subscriptions to sync</div>
                    </div>
                  </div>

                  {/* Customer List Preview */}
                  {preview.customers_preview.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-3">
                        Customers to be imported:
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                        {preview.customers_preview.map((customer, index) => (
                          <div key={index} className="p-4 border-b border-gray-200 last:border-b-0">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 font-medium text-sm">
                                      {customer.name ? customer.name[0].toUpperCase() : customer.email[0].toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">
                                      {customer.name || 'No name provided'}
                                    </div>
                                    <div className="text-sm text-gray-600">{customer.email}</div>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-600">
                                  {customer.subscriptions.length} subscription{customer.subscriptions.length !== 1 ? 's' : ''}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {customer.stripe_customer_id.slice(-8)}
                                </div>
                              </div>
                            </div>
                            {customer.subscriptions.length > 0 && (
                              <div className="mt-2 ml-11">
                                <div className="text-xs text-gray-500 mb-1">Active subscriptions:</div>
                                {customer.subscriptions.map((sub, subIndex) => (
                                  <div key={subIndex} className="text-xs bg-white px-2 py-1 rounded border inline-block mr-2 mb-1">
                                    {sub.status} • {typeof sub.product_id === 'string' ? sub.product_id.slice(-8) : 'Unknown'}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <button
                    onClick={handlePreview}
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2 mx-auto"
                  >
                    {loading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4" />
                    )}
                    <span>{loading ? 'Loading Preview...' : 'Preview Import'}</span>
                  </button>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-1 text-sm text-red-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              {preview && preview.customers_to_import > 0 && (
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {loading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    <span>{loading ? 'Importing...' : 'Import & Send Emails'}</span>
                  </button>
                </div>
              )}

              {preview && preview.customers_to_import === 0 && (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">No new customers to import!</p>
                  <button
                    onClick={handleClose}
                    className="mt-3 btn-primary"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Importing Customers...
              </h3>
              <p className="text-gray-600">
                This may take a few moments. Please don't close this window.
              </p>
            </div>
          )}

          {step === 'complete' && result && (
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Import Completed Successfully!
                </h3>
                <p className="text-gray-600">
                  {result.customers_created} customers have been imported and emails sent.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{result.customers_created}</div>
                  <div className="text-sm text-green-600">Customers imported</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{result.subscriptions_synced}</div>
                  <div className="text-sm text-blue-600">Subscriptions synced</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{result.emails_sent}</div>
                  <div className="text-sm text-green-600">Emails sent</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{result.emails_failed}</div>
                  <div className="text-sm text-red-600">Emails failed</div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">
                    Warnings ({result.errors.length})
                  </h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {result.errors.slice(0, 3).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                    {result.errors.length > 3 && (
                      <li>• ... and {result.errors.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleClose}
                  className="btn-primary"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportStripeCustomersModal; 